import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { BoardType } from '@/shared/constants/app/routes.constant.ts';
import {
  ONE_MINUTE_MS,
  STALE_TIME,
} from '@/shared/constants/app/time.constant.ts';
import { boardApi } from './board.api.ts';
import { boardKeys } from './board.keys.ts';
import type { BoardListParams, Paginated, Post } from '../types/board.type.ts';

/**
 * 게시판 목록 조회 훅 (offset 페이지네이션).
 *
 * keepPreviousData: 페이지/필터 변경 시 이전 데이터를 유지해
 * 페이지네이션 클릭마다 화면이 깜빡이거나 빈 상태로 점프하지 않게 한다.
 * (목록 UX 의 핵심 — 깊은 페이지 점프 시 체감 성능 ↑)
 */
export function useBoardList<T extends Post = Post>(
  type: BoardType,
  params: BoardListParams,
) {
  return useQuery({
    queryKey: boardKeys.list(type, params),
    queryFn: () => boardApi.getList(type, params) as Promise<Paginated<T>>,
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME.SHORT,
  });
}

/**
 * 게시판 상세 조회 훅.
 * body(jsonb) + attachments 를 포함한 전체 글을 가져온다.
 */
export function useBoardDetail<T extends Post = Post>(
  type: BoardType,
  id: string | number | undefined,
) {
  return useQuery({
    queryKey: boardKeys.detail(type, id ?? ''),
    queryFn: () => boardApi.getDetail(type, id!) as Promise<T>,
    enabled: id !== undefined && id !== '',
    staleTime: ONE_MINUTE_MS,
  });
}
